import classNames from "classnames";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import Button from "components/button/Button";
import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import ExportIcon from "components/icons/ExportIcon";
import Info from "components/icons/Info";
import CircleNumber from "components/products/CircleWithNumber";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import StepHeader from "components/products/StepHeader";
import { BarOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { OnboardingImage, SoftwarePackage } from "domain/softwarePackages";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

interface Props {
    stepIndex: number;
    menuExpanded: boolean;
    stepCompleted: boolean;
    stepActive: boolean;
    stepClickable: boolean;
    saveOnboardingStep: (onboarding: Onboarding) => void;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
    fetchedOnboardingImages?: OnboardingImage[];
    fetchedSoftwarePackages?: SoftwarePackage[];
}

interface StepAction {
    description?: string;
    label: string;
    onClick: () => void;
}

interface Step {
    description: string | JSX.Element;
    actions?: StepAction[];
    subSteps?: string[];
}

const CreateCaptureImage = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [showContent, setShowContent] = React.useState<boolean>(false);
    const stepName = BarOnboardingSteps.STEP_CREATE_CAPTURE_IMAGE;

    const clickEvent = () => {
        props.stepClickable && setShowContent(!showContent);
    };

    const saveOnboardingStep = () => {
        props.saveOnboardingStep({
            product: OnboardingProductType.BAR,
            step: BarOnboardingSteps.STEP_CREATE_CAPTURE_IMAGE,
        });
    };

    React.useEffect(() => {
        if (props.stepInProgressIndex !== props.stepIndex) {
            setShowContent(false);
        }
    }, [props.stepInProgressIndex]);

    React.useEffect(() => {
        if (props.menuExpanded && props.stepClickable) {
            setShowContent(true);
        }
    }, [props.menuExpanded]);

    const steps: Step[] = [
        {
            description: t("Onboarding.barHomePage.createCaptureImage.steps.downloadWinBuilder"),
        },
        {
            description: t("Onboarding.barHomePage.createCaptureImage.steps.selectAdvanced"),
        },
        {
            description: t("Onboarding.barHomePage.createCaptureImage.steps.destinationPath"),
            actions: [
                {
                    label: "\\\\10.0.1.1\\images",
                    onClick: () => {},
                },
            ],
        },
        {
            description: t("Onboarding.barHomePage.createCaptureImage.steps.defaultOptions"),
        },
        {
            description: t("Onboarding.barHomePage.createCaptureImage.steps.selectMediaType"),
        },
        {
            description: (
                <Trans
                    i18nKey="Onboarding.barHomePage.createCaptureImage.steps.formatUsb"
                    values={{
                        linkText: t("Onboarding.barHomePage.createCaptureImage.steps.formatUsb"),
                    }}
                    components={[
                        <a
                            key="bestPractices"
                            href="https://support.blancco.com/space/KB/343605249/Best+Practices+for+Blancco+Asset+Image+Capture"
                            target="_blank"
                            rel="noopener noreferrer"
                        />,
                    ]}
                />
            ),
            subSteps: [
                t("Onboarding.barHomePage.createCaptureImage.steps.formatUsbSubSteps.connectUsb"),
                t("Onboarding.barHomePage.createCaptureImage.steps.formatUsbSubSteps.bootUsb"),
                t("Onboarding.barHomePage.createCaptureImage.steps.formatUsbSubSteps.installWindows"),
            ],
        },
        {
            description: t("Onboarding.barHomePage.createCaptureImage.steps.installWindows"),
        },
        {
            description: t("Onboarding.barHomePage.createCaptureImage.steps.rebootWithUsb"),
        },
        {
            description: t("Onboarding.barHomePage.createCaptureImage.steps.connectNetwork"),
        },
        {
            description: t("Onboarding.barHomePage.createCaptureImage.steps.captureWindows"),
        },
        {
            description: t("Onboarding.barHomePage.createCaptureImage.steps.transferIsos"),
            subSteps: [t("Onboarding.barHomePage.createCaptureImage.steps.instructionTransferIso")],
        },
    ];

    function createContent() {
        return (
            <>
                <div className={style.stepsContainer}>
                    {steps.map((step, index) => (
                        <div key={index}>
                            <div className={style.subStepsToFollowLayout}>
                                <CircleNumber number={index + 1} subStep={true} />
                                <div className={style.stepPadding}>
                                    <div>{step.description}</div>
                                    {step.subSteps && (
                                        <div>
                                            <ul className={style.listPadding}>
                                                {step.subSteps.map((subStep, index) => (
                                                    <li key={index}>{subStep}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {step.actions && step.actions.length > 0 && (
                                        <div className={style.columnLayout}>
                                            {step.actions.map((action, actionIndex) => {
                                                const isCopyAction =
                                                    action.label.includes("\\") || action.label.includes("https://");
                                                return (
                                                    <div key={actionIndex}>
                                                        <div>{action.description}</div>
                                                        {isCopyAction ? (
                                                            <div
                                                                className={classNames(
                                                                    style.stepActionButton,
                                                                    style.linuxCommandFont
                                                                )}
                                                            >
                                                                <div>{action.label}</div>
                                                                <div className={style.stepActionButtonWrapper}>
                                                                    <div className={style.actionButton}>
                                                                        <CopyToClipboard value={action.label} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className={style.stepActionButton}>
                                                                {action.label}
                                                                <div className={style.stepActionButtonWrapper}>
                                                                    <div className={style.actionButton}>
                                                                        <Button
                                                                            variant={"TERTIARY"}
                                                                            onClick={action.onClick}
                                                                        >
                                                                            <ExportIcon
                                                                                size={24}
                                                                                color={theme.iconFillColor}
                                                                            />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {index === 9 && (
                                <div className={style.alertBox}>
                                    <Info
                                        borderColor={theme.contentBackgroundColor}
                                        color={theme.sortIconActiveColor}
                                        size={"18"}
                                    />
                                    <div className={style.bannerText}>
                                        <Trans
                                            i18nKey="Onboarding.barHomePage.createCaptureImage.steps.knowledgeBaseInfo"
                                            components={[
                                                <a
                                                    key="knowledgeBase"
                                                    href="https://support.blancco.com/space/KB/349437954/How+to+mount+a+USB+drive+on+Ubuntu"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                />,
                                            ]}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className={style.markComplete}>
                    {props.stepCompleted ? (
                        <div className={style.templateSelectedBlock}>
                            <StepCompletedText
                                successText={t("Onboarding.gettingStartedFlow.completed")}
                                testId={testIds.workArea.onboarding.step.statusLabel}
                                dataType={OnboardingProductType.BAR + "_" + stepName}
                            />
                        </div>
                    ) : (
                        <Button
                            variant={"PRIMARY"}
                            loadingAnimation={props.stepInProgressIndex === props.stepIndex}
                            disabled={props.stepInProgressIndex === props.stepIndex}
                            className={classNames(buttons.primaryButtonWithoutIcon)}
                            onClick={() => {
                                props.saveStepInProgressIndex(props.stepIndex);
                                saveOnboardingStep();
                            }}
                            testId={testIds.workArea.onboarding.step.primaryButton}
                            data-type={OnboardingProductType.BAR + "_" + stepName}
                        >
                            {props.stepInProgressIndex === props.stepIndex
                                ? t("Onboarding.saving")
                                : t("Onboarding.barHomePage.createCaptureImage.markComplete")}
                        </Button>
                    )}
                </div>
            </>
        );
    }

    return (
        <StepHeader
            success={props.stepCompleted}
            showStep={showContent}
            inactive={!props.stepClickable}
            title={t("Onboarding.barHomePage.createCaptureImage.title")}
            stepIndex={props.stepIndex}
            menuExpanded={props.menuExpanded}
            showContent={showContent}
            clickEvent={clickEvent}
            content={createContent()}
            type={OnboardingProductType.BAR}
            stepName={stepName}
        />
    );
};

export default connector(CreateCaptureImage);
